/**
 * Changer de compte entre admins et comptes `@jeraisonne.fr` (edge fn
 * `admin-switch-account`, règle `admin-switch-rules`).
 *
 * Depuis un compte du groupe, le serveur autorise la bascule vers n'importe
 * quel autre : pas besoin de garder de session. SEUL le compte de revue store
 * (`app_tester@jeraisonne.fr`, code public) ne peut pas lancer de bascule :
 * pour en revenir, la session de départ est gardée sur l'appareil
 * (`jr_admin_return`). Garde-fous : ce retour n'est proposé QUE si le compte
 * connecté est exactement celui vers lequel on a basculé (`as_user_id`) et
 * pendant 12 h ; une déconnexion efface la sauvegarde.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export * from './admin-switch-rules';

const STORAGE_KEY = 'jr_admin_return';
export const ADMIN_RETURN_TTL_MS = 12 * 60 * 60 * 1000;

export interface AdminReturn {
  admin_id: string;
  access_token: string;
  refresh_token: string;
  as_user_id: string;
  as_name: string | null;
  saved_at: number;
}

export function readAdminReturn(): AdminReturn | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as AdminReturn;
    if (!v?.refresh_token || !v.as_user_id || !v.admin_id) return null;
    return v;
  } catch {
    return null;
  }
}

export function clearAdminReturn(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* stockage indisponible : rien à effacer */
  }
}

/** Retour possible seulement vers la session admin d'origine, pour le compte basculé, < 12 h. */
export function activeAdminReturn(
  saved: AdminReturn | null,
  currentUserId: string | null | undefined,
  now = Date.now(),
): AdminReturn | null {
  if (!saved || !currentUserId) return null;
  if (saved.as_user_id !== currentUserId) return null;
  if (now - saved.saved_at > ADMIN_RETURN_TTL_MS) return null;
  return saved;
}

export function adminSwitchErrorMessage(code: string): string {
  switch (code) {
    case 'not_internal':
      return 'Bascule possible seulement vers un compte @jeraisonne.fr.';
    case 'self':
      return 'C’est déjà ton compte.';
    case 'not_authorized':
      return 'Réservé aux admins.';
    case 'not_found':
      return 'Compte introuvable.';
    default:
      return 'La connexion à ce compte a échoué. Réessaie.';
  }
}

async function errorCode(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      return ((await ctx.json()) as { error?: string }).error ?? 'unknown';
    } catch {
      return 'unknown';
    }
  }
  return 'unknown';
}

export interface SwitchableAccount {
  user_id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  role: string | null;
  is_me: boolean;
}

/** Comptes du groupe (admins + @jeraisonne.fr) — seulement si le compte peut basculer. */
export function useSwitchableAccounts(enabled: boolean) {
  return useQuery({
    queryKey: ['switchable-accounts'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<SwitchableAccount[]> => {
      const { data, error } = await supabase.functions.invoke('admin-switch-account', {
        body: { action: 'list' },
      });
      if (error) throw new Error(adminSwitchErrorMessage(await errorCode(error)));
      return ((data as { accounts?: SwitchableAccount[] } | null)?.accounts ?? []).filter(
        (a) => !!a.user_id,
      );
    },
  });
}

/** Se connecter au compte `userId` (groupe), puis recharger l'app sur son espace. */
export async function switchToInternalAccount(userId: string): Promise<void> {
  const { data: current } = await supabase.auth.getSession();
  const adminSession = current.session;
  if (!adminSession) throw new Error(adminSwitchErrorMessage('not_authorized'));

  const { data, error } = await supabase.functions.invoke('admin-switch-account', {
    body: { action: 'switch', user_id: userId },
  });
  const res = data as {
    access_token?: string;
    refresh_token?: string;
    display_name?: string | null;
  } | null;
  if (error || !res?.access_token || !res.refresh_token) {
    throw new Error(adminSwitchErrorMessage(error ? await errorCode(error) : 'unknown'));
  }

  // On garde l'admin d'origine même si on enchaîne deux bascules.
  const previous = readAdminReturn();
  const origin =
    previous && previous.as_user_id === adminSession.user.id
      ? previous
      : {
          admin_id: adminSession.user.id,
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token,
        };
  const saved: AdminReturn = {
    admin_id: origin.admin_id,
    access_token: origin.access_token,
    refresh_token: origin.refresh_token,
    as_user_id: userId,
    as_name: res.display_name ?? null,
    saved_at: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch {
    throw new Error('Stockage de l’appareil indisponible : impossible de revenir ensuite.');
  }

  const { error: setErr } = await supabase.auth.setSession({
    access_token: res.access_token,
    refresh_token: res.refresh_token,
  });
  if (setErr) {
    clearAdminReturn();
    throw new Error(adminSwitchErrorMessage('unknown'));
  }
  window.location.assign('/');
}

/** Revenir à la session admin gardée ; sinon déconnexion vers l'écran de connexion. */
export async function returnToAdmin(saved: AdminReturn): Promise<void> {
  clearAdminReturn();
  const { error } = await supabase.auth.setSession({
    access_token: saved.access_token,
    refresh_token: saved.refresh_token,
  });
  if (error) {
    await supabase.auth.signOut({ scope: 'local' });
    window.location.assign('/login');
    return;
  }
  window.location.assign('/');
}
